import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { setBackButtonUrl } from "../uiSlice";
import { getPointsPerMatch, parseTimeToSeconds, formatMmSs } from "../../utils/yogaPoints";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import { addReportToServer } from "../../functions/sendReport";
import { fetchTravelGameStatus, saveTravelGameProgress } from "../../utils/travelGameApi";
import { saveTravelSession, readTravelSession, clearTravelSession } from "../../utils/travelGameSession";
import GameHud from "../../components/game/GameHud";
import YogaThemedModal from "../../components/yoga/YogaThemedModal";
import RoundView from "./RoundView";
import "./travelGame.css";

function isDemoUser(user) {
  return user?.source === "DEMO" || user?.role === "DEMO" || user?.role === "demobypass";
}

function demoPlansUrl() {
  const base = (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "");
  return `${base || (typeof window !== "undefined" ? window.location.origin : "")}/plans`;
}

function getEnabledRounds(themeData) {
  const raw = Array.isArray(themeData?.rounds) ? themeData.rounds : [];
  return raw
    .filter((r) => r && r.enabled !== false)
    .map((r, i) => ({ ...r, roundNumber: Number(r.roundNumber) || i + 1 }));
}

export default function TravelGame() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { data: themeData, status, error: themeError } = useSelector((s) => s.theme);
  const user = useSelector((s) => s.auth.user);
  const adminToken = useSelector(selectAdminToken);

  const rounds = useMemo(() => getEnabledRounds(themeData), [themeData]);
  const totalRounds = rounds.length;
  const pointsPerRound = useMemo(() => getPointsPerMatch(themeData), [themeData]);
  const totalSec = useMemo(() => parseTimeToSeconds(themeData?.timer) || 1920, [themeData]);
  const isDemo = useMemo(() => isDemoUser(user), [user]);

  const [currentRound, setCurrentRound] = useState(0);
  const [points, setPoints] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [answers, setAnswers] = useState({});
  const [popupRound, setPopupRound] = useState(null);
  const [finished, setFinished] = useState(false);
  const [rulesOpen, setRulesOpen] = useState(false);
  const [resumed, setResumed] = useState(false);

  const pointsRef = useRef(0);
  const elapsedRef = useRef(0);
  const currentRoundRef = useRef(0);
  const answersRef = useRef({});
  const finishedRef = useRef(false);
  const reportIdRef = useRef(null);
  const popupTimerRef = useRef(null);

  useEffect(() => () => {
    if (popupTimerRef.current) window.clearTimeout(popupTimerRef.current);
  }, []);

  useEffect(() => {
    pointsRef.current = points;
  }, [points]);
  useEffect(() => {
    elapsedRef.current = elapsedSec;
  }, [elapsedSec]);
  useEffect(() => {
    currentRoundRef.current = currentRound;
  }, [currentRound]);
  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);
  useEffect(() => {
    finishedRef.current = finished;
  }, [finished]);

  useEffect(() => {
    dispatch(setBackButtonUrl(user?.backButtonRedirect || "/login?&save=true"));
  }, [dispatch, user?.backButtonRedirect]);

  const answersList = useCallback(() => {
    return Object.keys(answersRef.current)
      .map((k) => Number(k))
      .sort((a, b) => a - b)
      .map((rn) => answersRef.current[rn]?.answer)
      .filter((a) => a != null && String(a).trim() !== "");
  }, []);

  const pushReport = useCallback(() => {
    if (isDemo || !user) return;
    addReportToServer(user, {
      points: pointsRef.current,
      elapsedSeconds: elapsedRef.current,
      answers: answersList(),
      reportIdRef,
    });
  }, [isDemo, user, answersList]);

  const persist = useCallback(
    async (nextStatus, finishedRound) => {
      const snapshot = {
        currentRound: currentRoundRef.current,
        points: pointsRef.current,
        elapsedSec: elapsedRef.current,
        answers: answersRef.current,
        completed: nextStatus === "completed",
      };
      saveTravelSession(snapshot);
      // Persist to our own game table for every logged-in session (demo included);
      // the external leaderboard report (pushReport) still excludes demo users.
      if (!user || !adminToken) return;
      try {
        await saveTravelGameProgress(adminToken, user, {
          points: pointsRef.current,
          time: formatMmSs(elapsedRef.current),
          currentRound: currentRoundRef.current,
          totalRounds,
          status: nextStatus,
          round: finishedRound || undefined,
        });
      } catch (e) {
        console.error("travel game persist:", e);
      }
      pushReport();
    },
    [isDemo, user, adminToken, totalRounds, pushReport]
  );

  // Resume from server (source of truth) + local fallback.
  useEffect(() => {
    if (status !== "succeeded" || !themeData || totalRounds === 0 || resumed) return;
    let cancelled = false;

    (async () => {
      const local = readTravelSession();
      let resume = { ...local };

      if (user && adminToken) {
        try {
          const remote = await fetchTravelGameStatus(adminToken, user);
          if (remote?.exists) {
            const remoteAnswers = {};
            (remote.rounds || []).forEach((r) => {
              remoteAnswers[r.roundNumber] = {
                answer: r.answer,
                isCorrect: r.isCorrect,
                skipped: r.skipped,
                points: r.points,
                time: r.time,
              };
            });
            resume = {
              currentRound: Math.max(local.currentRound || 0, remote.currentRound || 0),
              points: Math.max(local.points || 0, remote.points || 0),
              elapsedSec: Math.max(local.elapsedSec || 0, parseTimeToSeconds(remote.time)),
              answers: { ...local.answers, ...remoteAnswers },
              completed: !!remote.completed,
            };
          }
        } catch (e) {
          console.error("travel game resume:", e);
        }
      }

      if (cancelled) return;

      setPoints(resume.points || 0);
      pointsRef.current = resume.points || 0;
      setElapsedSec(resume.elapsedSec || 0);
      elapsedRef.current = resume.elapsedSec || 0;
      setAnswers(resume.answers || {});
      answersRef.current = resume.answers || {};

      if (resume.completed) {
        setFinished(true);
        finishedRef.current = true;
      } else {
        const idx = Math.min(resume.currentRound || 0, totalRounds - 1);
        setCurrentRound(idx);
        currentRoundRef.current = idx;
      }
      setResumed(true);
    })();

    return () => {
      cancelled = true;
    };
  }, [status, themeData, totalRounds, resumed, isDemo, user, adminToken]);

  const finalizeGame = useCallback(() => {
    if (finishedRef.current) return;
    setFinished(true);
    finishedRef.current = true;
    setPopupRound(null);
    persist("completed");
    clearTravelSession();
    // Skip the completion popup — go straight to the leaderboard.
    navigate(`/travel-leaderboard${window.location.search || ""}`);
  }, [persist, navigate]);

  // Single countdown shared across all rounds.
  useEffect(() => {
    if (!resumed || finished) return undefined;
    const id = window.setInterval(() => {
      setElapsedSec((prev) => {
        const next = prev + 1;
        elapsedRef.current = next;
        if (next >= totalSec) {
          window.clearInterval(id);
          finalizeGame();
        }
        return next;
      });
    }, 1000);
    return () => window.clearInterval(id);
  }, [resumed, finished, totalSec, finalizeGame]);

  const recordRound = useCallback(
    (round, { answer, isCorrect, skipped }) => {
      const already = answersRef.current[round.roundNumber];
      const earned = isCorrect && !already?.isCorrect ? pointsPerRound : 0;

      if (earned > 0) {
        const nextPoints = pointsRef.current + earned;
        pointsRef.current = nextPoints;
        setPoints(nextPoints);
      }

      const roundResult = {
        roundNumber: round.roundNumber,
        state: round.state || "",
        answer: answer || "",
        isCorrect: !!isCorrect,
        skipped: !!skipped,
        points: isCorrect ? (already?.isCorrect ? already.points : earned) : 0,
        time: formatMmSs(elapsedRef.current),
      };

      const nextAnswers = { ...answersRef.current, [round.roundNumber]: roundResult };
      answersRef.current = nextAnswers;
      setAnswers(nextAnswers);

      // Advance resume pointer to the next round.
      const nextIndex = Math.min(currentRoundRef.current + 1, totalRounds);
      currentRoundRef.current = nextIndex;

      const isLast = currentRoundRef.current >= totalRounds;
      persist(isLast ? "completed" : "in_progress", roundResult);

      // On a correct answer, briefly show the green/read-only box (and the
      // now-unblurred image) before the round popup covers the screen.
      if (popupTimerRef.current) window.clearTimeout(popupTimerRef.current);
      if (isCorrect) {
        popupTimerRef.current = window.setTimeout(() => setPopupRound(round), 700);
      } else {
        setPopupRound(round);
      }
    },
    [pointsPerRound, totalRounds, persist]
  );

  const handleSolved = useCallback(
    (result) => recordRound(rounds[currentRound], { ...result, skipped: false }),
    [rounds, currentRound, recordRound]
  );

  const handleSkip = useCallback(
    () => recordRound(rounds[currentRound], { answer: "", isCorrect: false, skipped: true }),
    [rounds, currentRound, recordRound]
  );

  const handlePopupNext = useCallback(() => {
    setPopupRound(null);

    // Demo users may only play the first stage, then go to plans.
    if (isDemo && currentRound === 0) {
      Swal.fire({
        icon: "success",
        title: "Thank you for playing.",
        text: "Subscribe to any PLAN to play with your peers.",
      }).then(() => {
        window.location.href = demoPlansUrl();
      });
      return;
    }

    const next = currentRound + 1;
    if (next >= totalRounds) {
      finalizeGame();
    } else {
      setCurrentRound(next);
      currentRoundRef.current = next;
    }
  }, [currentRound, totalRounds, finalizeGame, isDemo]);

  if (status === "loading" || status === "idle" || (!resumed && !finished && totalRounds > 0)) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  if (status === "failed" || !themeData) {
    return (
      <div className="tg-page">
        <p style={{ textAlign: "center", color: "#1a2b6d" }}>
          Could not load game data. Make sure the backend is running.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button
          type="button"
          className="tg-submit"
          style={{ margin: "16px auto", display: "block" }}
          onClick={() => dispatch(fetchThemeData({ themeId: null }))}
        >
          Retry
        </button>
      </div>
    );
  }

  if (totalRounds === 0) {
    return (
      <div className="tg-page">
        <p style={{ textAlign: "center" }}>No rounds configured for this game yet.</p>
      </div>
    );
  }

  const round = rounds[Math.min(currentRound, totalRounds - 1)];
  const rules = Array.isArray(themeData?.rules) ? themeData.rules : [];
  // Reveal popup: background uses the round bannerColor; the Next button uses
  // the admin button colors (button_background_color / button_text_color).
  const popupBg = popupRound?.bannerColor || round?.bannerColor || "#5a7a1e";
  const popupBtnBg = themeButtonBackground(themeData) || "#e05c3a";
  const popupBtnText = themeButtonTextColor(themeData) || "#ffffff";

  return (
    <div className="tg-page">
      <GameHud
        timeSec={elapsedSec}
        points={points}
        onRules={() => setRulesOpen(true)}
        buttonBg={popupBtnBg}
        buttonText={popupBtnText}
      />

      {!finished && round ? (
        <RoundView
          key={round.roundNumber}
          round={round}
          disabled={Boolean(popupRound)}
          onSolved={handleSolved}
          onSkip={handleSkip}
          buttonBg={popupBtnBg}
          buttonText={popupBtnText}
        />
      ) : null}

      {/* Per-round reveal popup — bannerColor background, admin-colored button. */}
      <YogaThemedModal
        open={Boolean(popupRound)}
        title={popupRound?.popup?.heading || popupRound?.state || "Well done!"}
        themeData={themeData}
        backgroundColor={popupBg}
        buttonBgColor={popupBtnBg}
        buttonTextColor={popupBtnText}
        confirmText={currentRound + 1 >= totalRounds ? "Finish" : "Next"}
        closeOnBackdrop={false}
        onConfirm={handlePopupNext}
      >
        <p>{popupRound?.popup?.body}</p>
      </YogaThemedModal>

      {/* Rules popup */}
      <YogaThemedModal
        open={rulesOpen}
        title="How to Play"
        themeData={themeData}
        confirmText="Got it"
        onConfirm={() => setRulesOpen(false)}
      >
        <ul>
          {rules.length > 0 ? (
            rules.map((r, i) => <li key={i}>{r}</li>)
          ) : (
            <li>Solve each stage to score points before the timer runs out.</li>
          )}
        </ul>
      </YogaThemedModal>

    </div>
  );
}
